import { alpha, createTheme, ThemeOptions } from "@mui/material";
import grey from "@mui/material/colors/grey";
import indigo from "@mui/material/colors/indigo";
import { UiStepIcon } from "@smp/ui";
import { Fragment } from "react";

declare module "@mui/material/Button" {
  interface ButtonPropsSizeOverrides {
    huge: true;
  }

  interface ButtonPropsVariantOverrides {
    dashed: true;
    semifilled: true;
    outfill: true;
  }

  interface ButtonPropsColorOverrides {
    grey: false;
    neutral: false;
    inherit: false;
    error: false;
    info: false;
    warning: false;
    white: true;
    paper: true;
  }
}

declare module "@mui/material/IconButton" {
  interface IconButtonPropsSizeOverrides {
    huge: true;
  }

  interface IconButtonPropsColorOverrides {
    grey: false;
    neutral: false;
    inherit: false;
    error: false;
    info: false;
    warning: false;
    white: true;
    paper: true;
  }
}

declare module "@mui/material/styles" {
  interface Palette {
    white: Palette["primary"];
    paper: Palette["primary"];
    neutral: Palette["primary"];
  }

  interface PaletteOptions {
    white?: PaletteOptions["primary"];
    paper?: PaletteOptions["primary"];
    neutral?: PaletteOptions["primary"];
  }
}

const { spacing, palette, typography } = createTheme({
  spacing: 8,
  palette: {
    background: {
      default: grey["100"],
    },
    default: {
      dark: "#6F7073",
      light: "#F3F5FB",
      main: "#BDBDBD",
    },
    error: {
      contrastText: "#ffffff",
      main: "#FF6E6E",
      dark: "#D65C5C",
      light: "#FFF4F4",
      400: "#FF8585",
      300: "#FFD4D4",
      200: "#FFE5E5",
      100: "#FFEDED",
    },
    info: {
      dark: indigo["700"],
      light: indigo["50"],
      main: indigo["500"],
    },
    primary: {
      main: "#3F51B5",
      dark: "#354498",
      light: "#F0F1F9",
      400: "#5E6DC1",
      200: "#DCE0F2",
    },
    secondary: {
      contrastText: "#ffffff",
      main: "#FF6E6E",
      dark: "#D65C5C",
      light: "#FFF4F4",
      400: "#FF8585",
      300: "#FFD4D4",
      200: "#FFE5E5",
      100: "#FFEDED",
    },
    success: {
      main: "#259B6D",
      dark: "#1F825B",
      light: "#EEF7F4",
      400: "#48AB84",
      300: "#BEE1D4",
      200: "#D8EDE5",
      100: "#E4F3ED",
    },
    warning: {
      main: "#FBC02D",
      light: "#FFFAEF",
      200: "#FEF4D9",
    },
    white: {
      contrastText: "#3F51B5",
      main: "#ffffff",
      dark: "#f2f2f2",
      light: "#ffffff",
    },
    paper: {
      contrastText: "#606163",
      main: "#ECEDEF",
      dark: "#C9CACB",
      light: "#F6F7F9",
    },
    neutral: {
      50: "#F6F7F9",
      100: "#F1F2F4",
      200: "#ECEDEF",
      300: "#E1E2E8",
      400: "#D2D4E0",
      600: "#AAAEC6",
    },
    grey: {
      300: "#DCDDDF",
      400: "#C9CACB",
      500: "#AAABAC",
      600: "#88898B",
      700: "#6F7072",
      800: "#606163",
      900: "#242527",
    },
  },
});

export const getThemeOptions = (isSSR?: boolean): ThemeOptions => ({
  breakpoints: {
    values: {
      xs: 0,
      sm: 768,
      md: 1024,
      lg: 1280,
      xl: 1866,
    },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: palette.background.default,
        },

        input: {
          fontFamily: typography.fontFamily,
        },

        textarea: {
          fontFamily: typography.fontFamily,
        },

        ".mui-pickers-MuiPickersStaticWrapper-root": {
          minWidth: "302px !important",
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          background: palette.neutral["200"],
          color: palette.grey[400],
        },
      },
    },
    MuiBadge: {
      styleOverrides: {
        badge: {
          borderRadius: spacing(0.5),
        },
      },
    },
    MuiButton: {
      variants: [
        /**
         * variant contained
         */
        {
          props: { variant: "contained", color: "primary" },
          style: {
            "&.Mui-disabled": {
              backgroundColor: "#B2B9E1",
              color: "#ffffff",
            },
          },
        },

        {
          props: { variant: "contained", color: "success" },
          style: {
            "&.Mui-disabled": {
              backgroundColor: "#A8D7C5",
              color: "#ffffff",
            },
          },
        },

        {
          props: { variant: "contained", color: "secondary" },
          style: {
            "&.Mui-disabled": {
              backgroundColor: "#FFC5C5",
              color: "#ffffff",
            },
          },
        },

        {
          props: { variant: "contained", color: "paper" },
          style: {
            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.paper.contrastText, 0.4),
            },
          },
        },

        {
          props: { variant: "contained", color: "white" },
          style: {
            "&.Mui-disabled": {
              backgroundColor: "#ffffff",
              color: alpha(palette.primary.main, 0.4),
            },
          },
        },

        /**
         * variant semifilled
         */
        {
          props: { variant: "semifilled" },
          style: {
            background: "#ECEDEF",
            color: palette.paper.contrastText,

            "&:hover": {
              background: "#E1E2E8",
            },
          },
        },

        {
          props: { variant: "semifilled", color: "primary" },
          style: {
            color: palette.primary.main,

            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.primary.main, 0.4),
            },
          },
        },

        {
          props: { variant: "semifilled", color: "success" },
          style: {
            color: palette.success.main,

            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.success.main, 0.4),
            },
          },
        },

        {
          props: { variant: "semifilled", color: "secondary" },
          style: {
            color: palette.secondary.main,

            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.secondary.main, 0.4),
            },
          },
        },

        {
          props: { variant: "semifilled", color: "paper" },
          style: {
            color: palette.paper.contrastText,

            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.paper.contrastText, 0.4),
            },
          },
        },

        {
          props: { variant: "semifilled", color: "white" },
          style: {
            color: palette.primary.main,

            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.primary.main, 0.4),
            },
          },
        },

        /**
         * variant outfill
         */
        {
          props: { variant: "outfill" },
          style: {
            borderWidth: 1,
            borderStyle: "solid",
          },
        },

        {
          props: { variant: "outfill", color: "primary" },
          style: {
            background: "#ecedef",
            color: palette.primary.main,

            "&:hover": {
              background: palette.neutral[200],
            },

            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.primary.main, 0.4),
            },
          },
        },

        {
          props: { variant: "outfill", color: "success" },
          style: {
            color: palette.success.main,
            background: palette.success[100],

            "&:hover": {
              background: palette.success[200],
            },

            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.success.main, 0.4),
            },
          },
        },

        {
          props: { variant: "outfill", color: "secondary" },
          style: {
            color: palette.secondary.main,
            background: palette.secondary.light,

            "&:hover": {
              background: palette.secondary[200],
            },

            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.secondary.main, 0.4),
            },
          },
        },

        {
          props: { variant: "outfill", color: "paper" },
          style: {
            background: palette.common.white,
            color: palette.paper.contrastText,

            "&:hover": {
              background: palette.neutral[200],
            },

            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.paper.contrastText, 0.4),
            },
          },
        },

        {
          props: { variant: "outfill", color: "white" },
          style: {
            color: palette.primary.main,
            background: palette.common.white,
            borderColor: palette.common.white,

            "&:hover": {
              background: palette.neutral[200],
            },

            "&.Mui-disabled": {
              backgroundColor: "#F7F8F9",
              color: alpha(palette.primary.main, 0.4),
            },
          },
        },

        /**
         * variant text
         */
        {
          props: { variant: "text", color: "primary" },
          style: {
            "&.Mui-disabled": {
              backgroundColor: "transparent",
              color: alpha(palette.primary.main, 0.4),
            },
          },
        },

        {
          props: { variant: "text", color: "success" },
          style: {
            background: "#E5F3ED",

            "&:hover": {
              background: "#D8EDE5",
            },

            "&.Mui-disabled": {
              backgroundColor: "#F5FAF8",
              color: alpha(palette.success.main, 0.4),
            },
          },
        },

        {
          props: { variant: "text", color: "secondary" },
          style: {
            background: "#FFF3F3",

            "&:hover": {
              background: "#FFE5E5",
            },

            "&.Mui-disabled": {
              backgroundColor: "#FFF8F8",
              color: alpha(palette.secondary.main, 0.4),
            },
          },
        },

        {
          props: { variant: "text", color: "paper" },
          style: {
            background: "transparent",
            color: palette.paper.contrastText,

            "&:hover": {
              background: "#ECEDEF",
            },
          },
        },

        {
          props: { variant: "text", color: "white" },
          style: {
            "&.Mui-disabled": {
              color: alpha(palette.white.main, 0.4),
            },
          },
        },

        /**
         * variant outlined
         */
        {
          props: { variant: "outlined", color: "primary" },
          style: {
            borderColor: palette.primary.main,

            "&.Mui-disabled": {
              backgroundColor: "transparent",
              color: alpha(palette.primary.main, 0.4),
            },
          },
        },

        {
          props: { variant: "outlined", color: "success" },
          style: {
            "&.Mui-disabled": {
              backgroundColor: "transparent",
              color: alpha(palette.success.main, 0.4),
              borderColor: alpha(palette.success.main, 0.4),
            },
          },
        },

        {
          props: { variant: "outlined", color: "secondary" },
          style: {
            "&.Mui-disabled": {
              backgroundColor: "transparent",
              color: alpha(palette.secondary.main, 0.4),
              borderColor: alpha(palette.secondary.main, 0.4),
            },
          },
        },

        {
          props: { variant: "outlined", color: "paper" },
          style: {
            borderColor: alpha(palette.paper.contrastText, 0.5),
            color: palette.paper.contrastText,

            "&:hover": {
              borderColor: palette.paper.contrastText,
              backgroundColor: alpha(palette.paper.contrastText, 0.04),
            },
          },
        },

        {
          props: { variant: "outlined", color: "white" },
          style: {
            "&.Mui-disabled": {
              backgroundColor: "transparent",
              color: alpha(palette.white.main, 0.4),
              borderColor: alpha(palette.white.main, 0.4),
            },
          },
        },

        /**
         * variant dashed
         */
        {
          props: { variant: "dashed" },
          style: {
            borderColor: "currentColor",
            borderWidth: 1,
            borderStyle: "dashed",
            padding: "15px 19px",
          },
        },

        {
          props: { variant: "dashed", color: "primary" },
          style: {
            color: palette.primary.main,

            "&:hover": {
              backgroundColor: alpha(palette.primary.main, 0.04),
            },

            "&.Mui-disabled": {
              backgroundColor: "transparent",
              color: alpha(palette.primary.main, 0.4),
            },
          },
        },

        {
          props: { variant: "dashed", color: "success" },
          style: {
            color: palette.success.main,

            "&:hover": {
              backgroundColor: alpha(palette.success.main, 0.04),
            },

            "&.Mui-disabled": {
              backgroundColor: "transparent",
              color: alpha(palette.success.main, 0.4),
            },
          },
        },

        {
          props: { variant: "dashed", color: "secondary" },
          style: {
            color: palette.secondary.main,

            "&:hover": {
              backgroundColor: alpha(palette.secondary.main, 0.04),
            },

            "&.Mui-disabled": {
              backgroundColor: "transparent",
              color: alpha(palette.secondary.main, 0.4),
            },
          },
        },

        {
          props: { variant: "dashed", color: "paper" },
          style: {
            color: palette.paper.contrastText,

            "&:hover": {
              backgroundColor: alpha(palette.paper.contrastText, 0.04),
            },

            "&.Mui-disabled": {
              backgroundColor: "transparent",
              color: alpha(palette.paper.contrastText, 0.4),
            },
          },
        },

        {
          props: { variant: "dashed", color: "white" },
          style: {
            color: palette.white.main,

            "&:hover": {
              backgroundColor: alpha(palette.white.main, 0.04),
            },

            "&.Mui-disabled": {
              backgroundColor: "transparent",
              color: alpha(palette.white.main, 0.4),
            },
          },
        },

        /**
         * size small
         */
        {
          props: { size: "small" },
          style: {
            fontSize: 12,
            fontWeight: 500,
            padding: "4px 6px",
            borderRadius: 8,
            minWidth: 32,
            minHeight: 32,

            "& .MuiButton-startIcon": {
              marginRight: 4,
            },

            "& .MuiButton-endIcon": {
              marginLeft: 4,
            },

            "& .MuiButton-startIcon .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)":
              {
                fontSize: 24,
              },

            "& .MuiButton-endIcon .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)":
              {
                fontSize: 24,
              },

            "& .MuiButton-startIcon .tabler-icon": {
              width: 24,
              height: 24,
            },

            "& .MuiButton-endIcon .tabler-icon": {
              width: 24,
              height: 24,
            },
          },
        },

        {
          props: { size: "small", variant: "outlined" },
          style: {
            padding: "3px 5px",
          },
        },

        {
          props: { size: "small", variant: "dashed" },
          style: {
            padding: "3px 5px",
          },
        },

        {
          props: { size: "small", variant: "outfill" },
          style: {
            padding: "3px 5px",
          },
        },

        /**
         * size medium
         */
        {
          props: { size: "medium" },
          style: {
            fontSize: 14,
            padding: "8px 12px",
            minWidth: 40,
            minHeight: 40,

            "& .MuiButton-startIcon .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)":
              {
                fontSize: 24,
              },

            "& .MuiButton-endIcon .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)":
              {
                fontSize: 24,
              },
          },
        },

        {
          props: { size: "medium", variant: "outlined" },
          style: {
            padding: "7px 11px",
          },
        },

        {
          props: { size: "medium", variant: "dashed" },
          style: {
            padding: "7px 11px",
          },
        },

        {
          props: { size: "medium", variant: "outfill" },
          style: {
            padding: "7px 11px",
          },
        },

        /**
         * size large
         */
        {
          props: { size: "large" },
          style: {
            fontSize: 14,
            padding: "12px 20px",
            minWidth: 48,
            minHeight: 48,

            "& .MuiButton-startIcon .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)":
              {
                fontSize: 24,
              },

            "& .MuiButton-endIcon .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)":
              {
                fontSize: 24,
              },
          },
        },

        {
          props: { size: "large", variant: "outlined" },
          style: {
            padding: "11px 19px",
          },
        },

        {
          props: { size: "large", variant: "dashed" },
          style: {
            padding: "11px 19px",
          },
        },

        {
          props: { size: "large", variant: "outfill" },
          style: {
            padding: "11px 19px",
          },
        },

        /**
         * size huge
         */
        {
          props: { size: "huge" },
          style: {
            fontSize: 16,
            padding: "16px 24px",
            minWidth: 56,
            minHeight: 56,

            "& .MuiButton-startIcon .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)":
              {
                fontSize: 24,
              },

            "& .MuiButton-endIcon .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)":
              {
                fontSize: 24,
              },
          },
        },

        {
          props: { size: "huge", variant: "outlined" },
          style: {
            padding: "15px 23px",
          },
        },

        {
          props: { size: "huge", variant: "dashed" },
          style: {
            padding: "15px 23px",
          },
        },

        {
          props: { size: "huge", variant: "outfill" },
          style: {
            padding: "15px 23px",
          },
        },
      ],
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            opacity: 1,
            backgroundColor: undefined,

            "&:before": {
              content: "''",
            },
          },

          lineHeight: 1.275,
          borderRadius: 12,
          textTransform: "none",
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          fontFamily: "inherit",
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          alignSelf: "flex-start",
        },
      },
    },
    MuiIconButton: {
      variants: [
        {
          props: { color: "paper" },
          style: {
            color: palette.paper.contrastText,
          },
        },

        {
          props: { size: "small" },
          style: {
            padding: 4,

            "& .tabler-icon": {
              width: 24,
              height: 24,
            },
          },
        },

        {
          props: { size: "medium" },
          style: {
            padding: 8,

            "& .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)": {
              fontSize: 24,
            },

            "& .tabler-icon": {
              width: 24,
              height: 24,
            },
          },
        },

        {
          props: { size: "large" },
          style: {
            padding: 10,

            "& .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)": {
              fontSize: 28,
            },

            "& .tabler-icon": {
              width: 28,
              height: 28,
            },
          },
        },

        {
          props: { size: "huge" },
          style: {
            padding: 14,

            "& .MuiSvgIcon-root:not(.MuiSvgIcon-fontSizeInherit)": {
              fontSize: 28,
            },

            "& .tabler-icon": {
              width: 28,
              height: 28,
            },
          },
        },
      ],
      defaultProps: {
        color: "paper",
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 18,
          padding: spacing(4),
        },
        paperWidthSm: {
          maxWidth: 650,
        },
        paperFullScreen: {
          maxWidth: "100%",
          borderRadius: 0,
        },
      },
      defaultProps: {
        TransitionComponent: isSSR ? Fragment : undefined,
        BackdropProps: {
          TransitionComponent: isSSR ? Fragment : undefined,
        },
        transitionDuration: {
          exit: 0,
        },
      },
    },
    MuiDialogActions: {
      styleOverrides: {
        root: {
          padding: 0,
        },
      },
    },
    MuiDialogContent: {
      styleOverrides: {
        root: {
          padding: spacing(2, 3),
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          ".MuiBox-root + &": {
            paddingRight: spacing(6),
          },

          padding: "6px 0 16px",
          fontSize: 24,
          lineHeight: "28px",
          wordBreak: "break-word",
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          color: palette.error.main,
          wordBreak: "break-word",
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          cursor: "pointer",
          fontWeight: 500,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          alignItems: "stretch",
        },
        label: {
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        },
      },
      defaultProps: {
        componentsProps: {
          typography: {
            component: "div",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            color: "#D6D6D6",
          },

          fontSize: 16,
          color: "#999999",
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            color: "#999999",
            opacity: 1,
          },

          "&.Mui-disabled": {
            textFillColor: "#999999",

            "&::placeholder": {
              color: "#D6D6D6",
              textFillColor: "#D6D6D6",
              opacity: 1,
            },
          },
        },

        root: {
          fontSize: 16,
          borderRadius: 12,
          color: "#000000",

          "&.Mui-disabled": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "#C5C5C6",
            },
          },

          "&.Mui-error": {
            "& .MuiOutlinedInput-notchedOutline": {
              borderWidth: 2,
            },
          },

          "& .MuiSelect-select": {
            "&:focus": {
              borderRadius: 12,
            },
          },

          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "#6F6F71",
          },
        },

        inputMultiline: {
          "&::-webkit-scrollbar": {
            width: 0,
          },

          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#989898",
            position: "absolute",
            width: 4,
            borderRadius: "2px",
          },

          "&::-webkit-scrollbar-track": {
            background: "#ECEDEF",
            borderRadius: "2px",
          },

          "&::-webkit-scrollbar:horizontal": {
            height: 4,
          },

          "&::-webkit-scrollbar:vertical": {
            width: 4,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        elevation1: {
          boxShadow: "0 4px 4px 0 rgba(0, 0, 0, 0.08)",
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        root: {
          "& .placeholder": {
            opacity: 0.42,
          },
        },

        select: {
          paddingRight: 32,
          minHeight: "1.143em",
        },
      },
    },
    MuiStepConnector: {
      styleOverrides: {
        line: {
          borderColor: "#b2b9e1",
          borderTopWidth: 2,
          borderTopStyle: "dashed",
        },
        root: {
          "&.Mui-active": {
            "& .MuiStepConnector-line": {
              borderTopStyle: "solid",
              borderColor: palette.primary.main,
            },
          },

          "&.Mui-completed": {
            "& .MuiStepConnector-line": {
              borderTopStyle: "solid",
              borderColor: palette.primary.main,
            },
          },
        },
        active: {},
      },
    },
    MuiStep: {
      styleOverrides: {
        root: {
          "&:first-of-type": {
            paddingLeft: 0,
          },

          "&:last-of-type": {
            paddingRight: 0,
          },
        },
      },
      defaultProps: {
        component: "span",
      },
    },
    MuiStepLabel: {
      styleOverrides: {
        iconContainer: {
          padding: 0,
        },
      },
      defaultProps: {
        StepIconComponent: UiStepIcon,
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minHeight: "auto",
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          padding: "6px 12px",
          maxWidth: 264,
          lineHeight: 1.75,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: "#757575",
          fontSize: 14,
          fontWeight: 400,
        },
        root: {
          "&:first-child": {
            paddingLeft: 0,
          },

          "&:last-child": {
            paddingRight: 0,
          },

          padding: spacing(3, 2),
        },
        sizeSmall: {
          padding: "6px 24px 6px 16px",
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: "standard",
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          whiteSpace: "pre-line",
          textAlign: "center",
        },
      },
      defaultProps: {
        enterTouchDelay: 300,
      },
    },
    MuiSlider: {
      defaultProps: {
        size: "small",
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: 10,
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        padding: {
          padding: "16px 20px",
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: "#000000",
          minWidth: 24,
          marginRight: 8,
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          color: "#000000",
          fontSize: 16,
          lineHeight: "24px",
          margin: 0,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        adornedEnd: {
          "& .tabler-icon": {
            flexShrink: 0,
            color: palette.grey[800],
          },

          "& .MuiSvgIcon-root": {
            color: palette.grey[800],
          },
        },

        adornedStart: {
          "& .tabler-icon": {
            flexShrink: 0,
            color: palette.grey[800],
          },

          "& .MuiSvgIcon-root": {
            color: palette.grey[800],
          },
        },
      },
    },
  },
  palette,
  spacing,
  typography: {
    body1: {
      fontSize: 14,
      fontWeight: 400,
    },
    body2: {
      fontSize: 16,
      fontWeight: 400,
      color: palette.common.black,
    },
    button: {
      fontSize: 14,
      fontWeight: 500,
    },
    caption: {
      fontSize: 10,
      fontWeight: 500,
    },
    fontWeightBold: 700,
    fontWeightLight: 300,
    fontWeightMedium: 500,
    fontWeightRegular: 400,
    h1: {
      fontSize: 40,
      fontWeight: 400,
    },
    h2: {
      fontSize: 30,
      fontWeight: 500,
    },
    h3: {
      fontSize: 24,
      fontWeight: 500,
    },
    h4: {
      fontSize: 20,
      fontWeight: 500,
    },
    h5: {
      fontSize: 16,
      fontWeight: 500,
    },
    h6: {
      fontSize: 14,
      fontWeight: 500,
    },
    subtitle1: {
      fontSize: 14,
      fontWeight: 400,
    },
    subtitle2: {
      fontSize: 12,
      fontWeight: 400,
    },
  },
});
